/**
 * YourTicketsPanel — right column of /support (SCK-549 PR-8).
 *
 * Renders the caller's ticket list with the polished shell primitives
 * from Directory (PageHeader-style header + SkeletonTable while
 * loading + EmptyState when zero + ActionRow with hover-reveal View).
 *
 * SOC 2: error messages are truncated + non-leaky. No PII from the
 * ticket payload is ever written to console.error.
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { useNavigate } from "react-router-dom";

import {
  ActionRow,
  EmptyState,
  PageHeader,
  SkeletonTable,
} from "../../components/Directory/primitives";
import { dirTokens } from "../../components/Directory/tokens";
import { formatRelative } from "../../helpers/date";
import { listMyTickets } from "./api";
import {
  slaBadge,
  slaChipColor,
  statusPillColor,
  truncate,
} from "./statusHelpers";

const COLUMN_SPEC = [
  { label: "Key", mono: true, width: 110 },
  { label: "Summary" },
  { label: "Status", width: 130 },
  { label: "Updated", width: 130 },
  { label: "SLA", width: 150 },
  { label: "", align: "right", width: 60 },
];

export default function YourTicketsPanel() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTickets(null);
    setErr(null);
    listMyTickets()
      .then((data) => {
        if (!cancelled) setTickets(data);
      })
      .catch((e) => {
        if (cancelled) return;
        // Never surface the raw body — coord may include the request
        // in some validation errors. Give a short, actionable line.
        const msg =
          e?.status === 401
            ? "Sign in again to see your tickets."
            : e?.status === 403
              ? "You don't have permission to view support tickets."
              : "Couldn't load your tickets. Try again in a moment.";
        setErr(msg);
        setTickets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box data-testid="your-tickets-panel">
      <PageHeader
        title="Your tickets"
        subtitle="Every support conversation you've escalated to a human, with SLA at a glance."
      />

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {!tickets && !err && (
        <SkeletonTable columns={COLUMN_SPEC} rows={4} />
      )}

      {tickets && tickets.length === 0 && (
        <EmptyState
          icon={<ConfirmationNumberOutlinedIcon />}
          title="No tickets yet"
          description="Ask the bot on the left — it can file a ticket for you when a human needs to jump in."
          variant="empty"
        />
      )}

      {tickets && tickets.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: dirTokens.radius }}
        >
          <Table size="small" aria-label="Your support tickets">
            <TableHead>
              <TableRow>
                {COLUMN_SPEC.map((c, i) => (
                  <TableCell
                    key={c.label || `col-${i}`}
                    align={c.align || "left"}
                    sx={c.mono ? { fontFamily: "monospace" } : undefined}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((row) => {
                const badge = slaBadge(row.sla_due || row.slaDue);
                const key = row.key || row.id;
                return (
                  <ActionRow
                    key={key}
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/support/tickets/${key}`)}
                    aria-label={`View ticket ${key}`}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        color: "text.primary",
                      }}
                    >
                      {key}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                      >
                        {truncate(row.summary)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status || "Unknown"}
                        size="small"
                        color={statusPillColor(row.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={row.updated_at || row.updatedAt || ""}
                        arrow
                        enterDelay={400}
                      >
                        <span>
                          {formatRelative(
                            row.updated_at || row.updatedAt
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={badge.label}
                        size="small"
                        color={slaChipColor(badge.tone)}
                        variant={
                          badge.tone === "breached" ? "filled" : "outlined"
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        className="row-actions"
                        sx={{
                          display: "inline-flex",
                          color: "text.secondary",
                        }}
                      >
                        <Tooltip title="View ticket">
                          <IconButton
                            size="small"
                            aria-label={`View ticket ${key}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/support/tickets/${key}`);
                            }}
                          >
                            <ChevronRightIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </ActionRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
